/**
 * Health Check Service
 * Provides health status information for monitoring
 */

import logger from './Logger';

export interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  version: string;
  uptime: number;
  checks: {
    api: CheckResult;
    authentication: CheckResult;
    storage: CheckResult;
    memory: CheckResult;
  };
  details?: Record<string, any>;
}

export interface CheckResult {
  status: 'pass' | 'fail' | 'warn';
  message?: string;
  responseTime?: number;
  details?: any;
}

class HealthCheckService {
  private static instance: HealthCheckService;
  private startTime: number;
  private healthEndpoint = '/api/health';

  private constructor() {
    this.startTime = Date.now();
  }

  static getInstance(): HealthCheckService {
    if (!HealthCheckService.instance) {
      HealthCheckService.instance = new HealthCheckService();
    }
    return HealthCheckService.instance;
  }

  /**
   * Perform comprehensive health check
   */
  async getHealthStatus(): Promise<HealthStatus> {
    const checks = await Promise.all([
      this.checkAPI(),
      this.checkAuthentication(),
      this.checkStorage(),
      this.checkMemory()
    ]);

    const [api, authentication, storage, memory] = checks;
    
    // Determine overall status
    const hasFailure = checks.some(check => check.status === 'fail');
    const hasWarning = checks.some(check => check.status === 'warn');
    
    let overallStatus: 'healthy' | 'degraded' | 'unhealthy';
    if (hasFailure) {
      overallStatus = 'unhealthy';
    } else if (hasWarning) {
      overallStatus = 'degraded';
    } else {
      overallStatus = 'healthy';
    }

    return {
      status: overallStatus,
      timestamp: new Date().toISOString(),
      version: process.env.REACT_APP_VERSION || '0.1.0',
      uptime: this.getUptime(),
      checks: {
        api,
        authentication,
        storage,
        memory
      },
      details: {
        environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
        buildTime: process.env.REACT_APP_BUILD_TIME,
        commitHash: process.env.REACT_APP_COMMIT_HASH
      }
    };
  }

  /**
   * Check API connectivity
   */
  private async checkAPI(): Promise<CheckResult> {
    const startTime = performance.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/health`, {
        method: 'GET',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const responseTime = performance.now() - startTime;
      
      if (response.ok) {
        return {
          status: 'pass',
          message: 'API is reachable',
          responseTime,
          details: {
            statusCode: response.status
          }
        };
      } else if (response.status >= 500) {
        return {
          status: 'fail',
          message: `API returned error: ${response.status}`,
          responseTime,
          details: {
            statusCode: response.status
          }
        };
      } else {
        return {
          status: 'warn',
          message: `API returned status: ${response.status}`,
          responseTime,
          details: {
            statusCode: response.status
          }
        };
      }
    } catch (error: any) {
      const responseTime = performance.now() - startTime;
      
      if (error.name === 'AbortError') {
        return {
          status: 'fail',
          message: 'API request timeout',
          responseTime
        };
      }
      
      return {
        status: 'fail',
        message: `API check failed: ${error.message}`,
        responseTime
      };
    }
  }

  /**
   * Check authentication status
   */
  private async checkAuthentication(): Promise<CheckResult> {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        return {
          status: 'pass',
          message: 'No active session'
        };
      }
      
      // Check if token is expired
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp * 1000;
        const now = Date.now();
        
        if (expiry < now) {
          return {
            status: 'warn',
            message: 'Token expired',
            details: {
              expiredAt: new Date(expiry).toISOString()
            }
          };
        }
        
        const timeUntilExpiry = expiry - now;
        const hoursUntilExpiry = timeUntilExpiry / (1000 * 60 * 60);
        
        if (hoursUntilExpiry < 1) {
          return {
            status: 'warn',
            message: 'Token expiring soon',
            details: {
              expiresIn: `${Math.floor(timeUntilExpiry / (1000 * 60))} minutes`
            }
          };
        }
        
        return {
          status: 'pass',
          message: 'Authentication valid',
          details: {
            expiresAt: new Date(expiry).toISOString()
          }
        };
      } catch (error) {
        return {
          status: 'fail',
          message: 'Invalid token format'
        };
      }
    } catch (error: any) {
      return {
        status: 'fail',
        message: `Authentication check failed: ${error.message}`
      };
    }
  }

  /**
   * Check localStorage availability and quota
   */
  private async checkStorage(): Promise<CheckResult> {
    try {
      // Test localStorage availability
      const testKey = '__healthcheck_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      
      // Estimate storage usage
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 0;
        const percentUsed = (usage / quota) * 100;
        
        if (percentUsed > 90) {
          return {
            status: 'warn',
            message: 'Storage nearly full',
            details: {
              usage: this.formatBytes(usage),
              quota: this.formatBytes(quota),
              percentUsed: percentUsed.toFixed(2)
            }
          };
        }
        
        return {
          status: 'pass',
          message: 'Storage available',
          details: {
            usage: this.formatBytes(usage),
            quota: this.formatBytes(quota),
            percentUsed: percentUsed.toFixed(2)
          }
        };
      }
      
      return {
        status: 'pass',
        message: 'Storage available'
      };
    } catch (error: any) {
      return {
        status: 'fail',
        message: `Storage check failed: ${error.message}`
      };
    }
  }

  /**
   * Check memory usage
   */
  private async checkMemory(): Promise<CheckResult> {
    try {
      // Check if performance.memory is available (Chrome only)
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        const usedHeap = memory.usedJSHeapSize;
        const totalHeap = memory.jsHeapSizeLimit;
        const percentUsed = (usedHeap / totalHeap) * 100;
        
        if (percentUsed > 90) {
          return {
            status: 'warn',
            message: 'High memory usage',
            details: {
              used: this.formatBytes(usedHeap),
              limit: this.formatBytes(totalHeap),
              percentUsed: percentUsed.toFixed(2)
            }
          };
        }
        
        return {
          status: 'pass',
          message: 'Memory usage normal',
          details: {
            used: this.formatBytes(usedHeap),
            limit: this.formatBytes(totalHeap),
            percentUsed: percentUsed.toFixed(2)
          }
        };
      }
      
      return {
        status: 'pass',
        message: 'Memory monitoring not available'
      };
    } catch (error: any) {
      return {
        status: 'warn',
        message: `Memory check failed: ${error.message}`
      };
    }
  }

  /**
   * Get application uptime in seconds
   */
  private getUptime(): number {
    return Math.floor((Date.now() - this.startTime) / 1000);
  }

  /**
   * Format bytes to human readable format
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Create health check endpoint handler
   */
  async handleHealthRequest(): Promise<Response> {
    try {
      const health = await this.getHealthStatus();
      
      const statusCode = health.status === 'healthy' ? 200 : 
                        health.status === 'degraded' ? 503 : 500;
      
      return new Response(JSON.stringify(health), {
        status: statusCode,
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
    } catch (error) {
      logger.error('Health check failed:', error);
      
      return new Response(JSON.stringify({
        status: 'unhealthy',
        error: 'Health check failed',
        timestamp: new Date().toISOString()
      }), {
        status: 500,
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
    }
  }

  /**
   * Start periodic health checks
   */
  startPeriodicHealthCheck(intervalMs: number = 60000): void {
    setInterval(async () => {
      const health = await this.getHealthStatus();
      
      if (health.status === 'unhealthy') {
        logger.error('Health check failed:', health);
        // Could trigger alerts here
      } else if (health.status === 'degraded') {
        logger.warn('Health check degraded:', health);
      } else {
        logger.debug('Health check passed:', health);
      }
    }, intervalMs);
  }
}

// Export singleton instance
const healthCheckService = HealthCheckService.getInstance();
export default healthCheckService;